/**
 * Client-side validation of the tutor-agent `rail_action` rich event
 * (docs/ai-tutor/streaming-events.md). The server already whitelists, but the
 * client never trusts the wire: only references travel, so only references are
 * accepted. `insert_code`, `scroll_to` and `seek_video` are never accepted
 * (they can carry content or move the learner without a verifiable reference).
 */

export type RailAction =
  | { kind: 'highlight_line'; line: number; test?: string }
  | { kind: 'open_chapter'; ref: string; source?: 'coursebook' | 'transcript' }

export const MAX_RAIL_ACTIONS_PER_TURN = 4
const MAX_LINE = 100_000
const MAX_TEXT = 120
const SOURCES: ReadonlySet<string> = new Set(['coursebook', 'transcript'])

function clean(value: string): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, MAX_TEXT)
}

export function normalizeRailAction(data: unknown): RailAction | null {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return null
  const d = data as Record<string, unknown>
  if (d.kind === 'highlight_line') {
    const line = d.line
    if (typeof line !== 'number' || !Number.isInteger(line) || line < 1 || line > MAX_LINE) return null
    const test = typeof d.test === 'string' ? clean(d.test) : ''
    return { kind: 'highlight_line', line, ...(test ? { test } : {}) }
  }
  if (d.kind === 'open_chapter') {
    const ref = typeof d.ref === 'string' ? clean(d.ref) : ''
    if (!ref) return null
    const source = typeof d.source === 'string' && SOURCES.has(d.source) ? d.source : undefined
    return { kind: 'open_chapter', ref, ...(source ? { source: source as 'coursebook' | 'transcript' } : {}) }
  }
  return null
}
